import React, { useEffect, useMemo, useRef, useState } from 'react';
import html2canvas from 'html2canvas';

const PRINT_HTML_KEY = 'vakra_print_html';
const PRINT_TITLE_KEY = 'vakra_print_title';
const PRINT_RETURN_URL_KEY = 'vakra_print_return_url';
const PRINT_CREATED_AT_KEY = 'vakra_print_created_at';

const getSavedPaperSize = () => {
  try {
    const saved = localStorage.getItem('fastfood_ui_settings');
    const parsed = saved ? JSON.parse(saved) : {};

    return parsed?.paperSize === '80mm' ? '80mm' : '58mm';
  } catch {
    return '58mm';
  }
};

const extractPrintParts = (html) => {
  const source = String(html || '');

  const styleMatches = source.match(/<style[\s\S]*?<\/style>/gi) || [];
  const styles = styleMatches
    .map((styleTag) =>
      styleTag
        .replace(/<\/?style[^>]*>/gi, '')
        .trim()
    )
    .filter(Boolean)
    .join('\n\n');

  const bodyMatch = source.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  let bodyHtml = bodyMatch?.[1] || source;

  bodyHtml = bodyHtml
    .replace(/<!doctype[^>]*>/gi, '')
    .replace(/<html[\s\S]*?>/gi, '')
    .replace(/<\/html>/gi, '')
    .replace(/<head[\s\S]*?<\/head>/gi, '')
    .replace(/<body[^>]*>/gi, '')
    .replace(/<\/body>/gi, '')
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<title[\s\S]*?<\/title>/gi, '')
    .replace(
      /<div[^>]*class=["'][^"']*screen-actions[^"']*["'][^>]*>[\s\S]*?<\/div>/gi,
      ''
    )
    .replace(/onclick=["']window\.print\(\)["']/gi, '')
    .replace(/onclick=["']window\.close\(\)["']/gi, '');

  return {
    styles,
    bodyHtml,
  };
};

const PrintPreview = () => {
  const returnTimerRef = useRef(null);
  const returnedRef = useRef(false);
  const printStartedRef = useRef(false);

  const [printStarted, setPrintStarted] = useState(false);
  const [paperSize] = useState(() => getSavedPaperSize());
  const [shareLoading, setShareLoading] = useState(false);
  const printShellRef = useRef(null);

  const printData = useMemo(() => {
    const html = sessionStorage.getItem(PRINT_HTML_KEY) || '';
    const title = sessionStorage.getItem(PRINT_TITLE_KEY) || 'Print Preview';
    const returnUrl =
      sessionStorage.getItem(PRINT_RETURN_URL_KEY) || '/ordersReceived';
    const createdAt = Number(sessionStorage.getItem(PRINT_CREATED_AT_KEY) || 0);

    return {
      html,
      title,
      returnUrl,
      createdAt,
    };
  }, []);

  const { styles: originalStyles, bodyHtml } = useMemo(
    () => extractPrintParts(printData.html),
    [printData.html]
  );

  const goBackToOrders = () => {
    if (returnedRef.current) return;

    returnedRef.current = true;
    window.clearTimeout(returnTimerRef.current);

    sessionStorage.removeItem(PRINT_HTML_KEY);
    sessionStorage.removeItem(PRINT_TITLE_KEY);
    sessionStorage.removeItem(PRINT_RETURN_URL_KEY);
    sessionStorage.removeItem(PRINT_CREATED_AT_KEY);

    window.location.replace(printData.returnUrl || '/ordersReceived');
  };

  const scheduleBack = (delay = 1100) => {
    if (!printStartedRef.current) return;
    if (returnedRef.current) return;

    window.clearTimeout(returnTimerRef.current);
    returnTimerRef.current = window.setTimeout(goBackToOrders, delay);
  };

  useEffect(() => {
    document.title = printData.title || 'Print Preview';

    const isOldData =
      printData.createdAt && Date.now() - printData.createdAt > 1000 * 60 * 10;

    const looksLikeOrdersUi =
      printData.html.includes('Orders Queue') ||
      printData.html.includes('Fast, simple order flow') ||
      printData.html.includes('Search table, amount, name, mobile');

    const looksLikeBillOrKot =
      printData.html.includes('kot-print') ||
      printData.html.includes('bill-print') ||
      printData.html.includes('thermal-bill') ||
      printData.html.includes('KOT') ||
      printData.html.includes('Invoice') ||
      printData.html.includes('Bill');

    if (!printData.html || isOldData || (looksLikeOrdersUi && !looksLikeBillOrKot)) {
      sessionStorage.removeItem(PRINT_HTML_KEY);
      sessionStorage.removeItem(PRINT_TITLE_KEY);
      sessionStorage.removeItem(PRINT_RETURN_URL_KEY);
      sessionStorage.removeItem(PRINT_CREATED_AT_KEY);
    }
  }, [printData.html, printData.title, printData.createdAt]);

  useEffect(() => {
    const handleAfterPrint = () => scheduleBack(550);

    const handleFocus = () => scheduleBack(1200);

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        scheduleBack(1200);
      }
    };

    let mediaQueryList = null;
    let mediaListener = null;

    window.addEventListener('afterprint', handleAfterPrint);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);

    try {
      mediaQueryList = window.matchMedia?.('print');
      mediaListener = (event) => {
        if (!event.matches) {
          scheduleBack(800);
        }
      };

      if (mediaQueryList?.addEventListener) {
        mediaQueryList.addEventListener('change', mediaListener);
      } else if (mediaQueryList?.addListener) {
        mediaQueryList.addListener(mediaListener);
      }
    } catch {}

    return () => {
      window.removeEventListener('afterprint', handleAfterPrint);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);

      if (mediaQueryList && mediaListener) {
        if (mediaQueryList.removeEventListener) {
          mediaQueryList.removeEventListener('change', mediaListener);
        } else if (mediaQueryList.removeListener) {
          mediaQueryList.removeListener(mediaListener);
        }
      }

      window.clearTimeout(returnTimerRef.current);
    };
  }, []);

  const escapeHtml = (value) =>
    String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

  const handlePrint = () => {
    if (printStartedRef.current) return;

    /*
      User click ke isi synchronous event me window open karna zaruri hai.
      Isse mobile browser popup ko block nahi karega.
    */
    const printWindow = window.open('', '_blank');

    if (!printWindow) {
      window.alert(
        'Print window block ho gayi. Browser me pop-ups allow karke dobara Print Bill dabayein.'
      );
      return;
    }

    printStartedRef.current = true;
    setPrintStarted(true);

    try {
      const cleanTitle = escapeHtml(printData.title || 'Vendor Bill');

      const isolatedPrintHtml = `
        <!doctype html>
        <html>
          <head>
            <meta charset="utf-8" />
            <meta
              name="viewport"
              content="width=device-width, initial-scale=1, maximum-scale=1"
            />
            <title>${cleanTitle}</title>

            <style>
              ${originalStyles || ''}

              *,
              *::before,
              *::after {
                box-sizing: border-box !important;
              }

              @page {
                size: ${paperSize} auto;
                margin: 0;
              }

              html,
              body {
                width: ${paperSize} !important;
                max-width: ${paperSize} !important;
                min-width: ${paperSize} !important;
                margin: 0 !important;
                padding: 0 !important;
                background: #ffffff !important;
                color: #000000 !important;
                overflow: visible !important;
                font-family: Arial, Helvetica, sans-serif;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }

              body {
                display: block !important;
              }

              .vakra-only-receipt {
                display: block !important;
                width: ${paperSize} !important;
                max-width: ${paperSize} !important;
                min-width: ${paperSize} !important;
                margin: 0 !important;
                padding: 0 !important;
                background: #ffffff !important;
                overflow: visible !important;
              }

              .vakra-only-receipt *,
              .vakra-only-receipt *::before,
              .vakra-only-receipt *::after {
                box-sizing: border-box !important;
                max-width: 100% !important;
              }

              .vakra-only-receipt .kot,
              .vakra-only-receipt .bill,
              .vakra-only-receipt .receipt,
              .vakra-only-receipt .kot-print,
              .vakra-only-receipt .bill-print,
              .vakra-only-receipt .thermal-bill,
              .vakra-only-receipt .print-page {
                display: block !important;
                width: 100% !important;
                max-width: 100% !important;
                min-width: 0 !important;
                margin: 0 !important;
                padding-left: 2mm !important;
                padding-right: 2mm !important;
                box-shadow: none !important;
                background: #ffffff !important;
                overflow: visible !important;
              }

              .vakra-only-receipt table {
                width: 100% !important;
                max-width: 100% !important;
                table-layout: fixed !important;
                border-collapse: collapse !important;
              }

              .vakra-only-receipt td,
              .vakra-only-receipt th {
                white-space: normal !important;
                overflow-wrap: anywhere !important;
                word-break: break-word !important;
              }

              .vakra-only-receipt img {
                display: block;
                max-width: 100% !important;
                height: auto !important;
              }

              .screen-actions,
              .print-preview-actions,
              .vakra-print-actions,
              .vakra-print-note,
              .vakra-share-fab,
              button.back-btn,
              button.print-btn {
                display: none !important;
              }

              @media print {
                html,
                body,
                .vakra-only-receipt {
                  width: ${paperSize} !important;
                  max-width: ${paperSize} !important;
                  min-width: ${paperSize} !important;
                  margin: 0 !important;
                  padding: 0 !important;
                  overflow: visible !important;
                  background: #ffffff !important;
                }
              }
            </style>
          </head>

          <body>
            <main class="vakra-only-receipt">
              ${bodyHtml}
            </main>

            <script>
              (function () {
                var printed = false;

                function runPrint() {
                  if (printed) return;
                  printed = true;

                  setTimeout(function () {
                    window.focus();
                    window.print();
                  }, 350);
                }

                var images = Array.prototype.slice.call(document.images || []);

                if (!images.length) {
                  runPrint();
                  return;
                }

                var remaining = images.length;

                function imageDone() {
                  remaining -= 1;
                  if (remaining <= 0) runPrint();
                }

                images.forEach(function (img) {
                  if (img.complete) {
                    imageDone();
                    return;
                  }

                  img.addEventListener('load', imageDone, { once: true });
                  img.addEventListener('error', imageDone, { once: true });
                });

                setTimeout(runPrint, 2500);
              })();
            </script>
          </body>
        </html>
      `;

      printWindow.document.open();
      printWindow.document.write(isolatedPrintHtml);
      printWindow.document.close();

      /*
        Print dialog close hone ke baad original Orders page par return timer.
        New print window ko force close nahi karenge, kyunki kuch mobile
        browsers window.close() ko block karte hain.
      */
      scheduleBack(5000);
    } catch (err) {
      console.error('Receipt print failed:', err);

      printStartedRef.current = false;
      setPrintStarted(false);

      try {
        printWindow.close();
      } catch {}

      window.alert(
        err?.message ||
          'Actual bill print nahi hua. Dobara Print Bill dabayein.'
      );
    }
  };

  const getSafeBillFileName = () => {
    const cleanTitle = String(printData.title || 'Vendor-Bill')
      .replace(/[^a-zA-Z0-9_-]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80);

    return `${cleanTitle || 'Vendor-Bill'}.png`;
  };

  const downloadBillImage = (blob, fileName) => {
    const imageUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = imageUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();

    window.setTimeout(() => URL.revokeObjectURL(imageUrl), 1500);
  };

  const handleShareBill = async () => {
    if (shareLoading) return;

    const receiptNode = printShellRef.current;

    if (!receiptNode) {
      window.alert('Bill preview ready nahi hai. Dobara try karein.');
      return;
    }

    try {
      setShareLoading(true);

      const canvas = await html2canvas(receiptNode, {
        backgroundColor: '#ffffff',
        scale: Math.max(2, Math.min(window.devicePixelRatio || 1, 3)),
        useCORS: true,
        allowTaint: false,
        logging: false,
        scrollX: 0,
        scrollY: -window.scrollY,
        windowWidth: receiptNode.scrollWidth,
        windowHeight: receiptNode.scrollHeight,
      });

      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (result) => {
            if (result) {
              resolve(result);
              return;
            }

            reject(new Error('Bill image create nahi hui.'));
          },
          'image/png',
          1
        );
      });

      const fileName = getSafeBillFileName();
      const billFile = new File([blob], fileName, {
        type: 'image/png',
        lastModified: Date.now(),
      });

      const shareData = {
        title: printData.title || 'Vendor Bill',
        text: 'Aapka bill attached hai.',
        files: [billFile],
      };

      if (
        navigator.share &&
        (!navigator.canShare || navigator.canShare({ files: [billFile] }))
      ) {
        await navigator.share(shareData);
        return;
      }

      downloadBillImage(blob, fileName);
      window.alert(
        'Is browser me direct file share support nahi hai. Bill image download ho gayi hai; use WhatsApp me attach karein.'
      );
    } catch (err) {
      if (err?.name === 'AbortError') return;

      console.error('Share bill failed:', err);
      window.alert(
        err?.message || 'Bill share nahi hua. Dobara try karein.'
      );
    } finally {
      setShareLoading(false);
    }
  };

  const isOldData =
    printData.createdAt && Date.now() - printData.createdAt > 1000 * 60 * 10;

  const looksLikeOrdersUi =
    printData.html.includes('Orders Queue') ||
    printData.html.includes('Fast, simple order flow') ||
    printData.html.includes('Search table, amount, name, mobile');

  const looksLikeBillOrKot =
    printData.html.includes('kot-print') ||
    printData.html.includes('bill-print') ||
    printData.html.includes('thermal-bill') ||
    printData.html.includes('KOT') ||
    printData.html.includes('Invoice') ||
    printData.html.includes('Bill');

  const invalidPrintData =
    !printData.html || isOldData || (looksLikeOrdersUi && !looksLikeBillOrKot);

  if (invalidPrintData) {
    return (
      <div className="vakra-print-preview-page">
        <style>
          {`
            .vakra-print-preview-page {
              min-height: 100vh;
              display: flex;
              align-items: center;
              justify-content: center;
              background: #f8fafc;
              padding: 20px;
              box-sizing: border-box;
              font-family: Arial, sans-serif;
              color: #111827;
            }

            .vakra-print-empty-box {
              width: 100%;
              max-width: 430px;
              background: #ffffff;
              border: 1px solid #e5e7eb;
              border-radius: 18px;
              padding: 22px;
              text-align: center;
              box-shadow: 0 14px 34px rgba(15, 23, 42, 0.12);
            }

            .vakra-print-empty-box h2 {
              margin: 0 0 8px;
              font-size: 22px;
            }

            .vakra-print-empty-box p {
              margin: 0;
              color: #475569;
              line-height: 1.5;
            }

            .vakra-print-empty-box button {
              margin-top: 16px;
              border: 0;
              border-radius: 10px;
              padding: 11px 14px;
              background: #111827;
              color: #ffffff;
              font-weight: 800;
              cursor: pointer;
            }
          `}
        </style>

        <div className="vakra-print-empty-box">
          <h2>Print data clear ho gaya</h2>
          <p>
            Purana ya galat Orders UI print data save tha. Orders page par wapas
            jaakar dobara Print Bill / Print KOT dabao.
          </p>
          <button type="button" onClick={goBackToOrders}>
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="vakra-print-preview-page">
      <style dangerouslySetInnerHTML={{ __html: originalStyles }} />

      <style>
        {`
          *,
          *::before,
          *::after {
            box-sizing: border-box !important;
          }

          html,
          body,
          #root {
            width: 100%;
            min-width: 0;
            margin: 0;
            padding: 0;
            overflow-x: hidden;
          }

          .vakra-print-preview-page {
            min-height: 100vh;
            width: 100%;
            background: #e5e7eb;
            padding: 72px 8px 24px;
            box-sizing: border-box;
            overflow-x: hidden;
            font-family: Arial, Helvetica, sans-serif;
          }

          .vakra-print-actions {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            z-index: 999999;
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 8px;
            padding: 10px;
            background: #111827;
            box-shadow: 0 8px 22px rgba(15, 23, 42, 0.22);
            box-sizing: border-box;
          }

          .vakra-print-actions button {
            border: 0;
            border-radius: 10px;
            padding: 11px 14px;
            font-size: 14px;
            font-weight: 800;
            line-height: 1;
            cursor: pointer;
            font-family: Arial, Helvetica, sans-serif;
          }

          .vakra-print-back {
            background: #ffffff;
            color: #111827;
          }

          .vakra-print-now {
            background: #16a34a;
            color: #ffffff;
          }

          .vakra-print-shell {
            width: ${paperSize};
            max-width: calc(100vw - 16px);
            margin: 0 auto;
            background: #ffffff;
            box-shadow: 0 14px 34px rgba(15, 23, 42, 0.24);
            overflow: hidden;
          }

          .vakra-print-content {
            width: 100%;
            max-width: 100%;
            min-width: 0;
            margin: 0 auto;
            padding: 0;
            background: #ffffff;
            overflow: hidden;
          }

          .vakra-print-content *,
          .vakra-print-content *::before,
          .vakra-print-content *::after {
            box-sizing: border-box !important;
            max-width: 100% !important;
          }

          .vakra-print-content .screen-actions,
          .vakra-print-content .print-preview-actions,
          .vakra-print-content button.back-btn,
          .vakra-print-content button.print-btn {
            display: none !important;
          }

          .vakra-print-content table {
            width: 100% !important;
            max-width: 100% !important;
            table-layout: fixed !important;
            border-collapse: collapse !important;
          }

          .vakra-print-content td,
          .vakra-print-content th {
            white-space: normal !important;
            overflow-wrap: anywhere !important;
            word-break: break-word !important;
          }

          .vakra-print-content img {
            max-width: 100% !important;
            height: auto !important;
          }

          .vakra-print-note {
            max-width: 430px;
            margin: 12px auto 0;
            text-align: center;
            color: #475569;
            font-size: 13px;
            line-height: 1.5;
            padding: 0 8px;
          }

          .vakra-share-fab {
            position: fixed;
            right: 16px;
            bottom: 16px;
            z-index: 999999;
            width: 54px;
            height: 54px;
            border: 0;
            border-radius: 999px;
            display: grid;
            place-items: center;
            background: #16a34a;
            color: #ffffff;
            box-shadow: 0 14px 32px rgba(22, 163, 74, 0.34);
            cursor: pointer;
            -webkit-tap-highlight-color: transparent;
          }

          .vakra-share-fab:hover {
            background: #15803d;
            transform: translateY(-1px);
          }

          .vakra-share-fab:active {
            transform: scale(0.96);
          }

          .vakra-share-fab:disabled {
            opacity: 0.65;
            cursor: wait;
          }

          .vakra-share-fab svg {
            width: 24px;
            height: 24px;
            fill: currentColor;
          }

          .vakra-share-spinner {
            width: 23px;
            height: 23px;
            border: 3px solid rgba(255, 255, 255, 0.42);
            border-top-color: #ffffff;
            border-radius: 999px;
            animation: vakraShareSpin 0.75s linear infinite;
          }

          @keyframes vakraShareSpin {
            to {
              transform: rotate(360deg);
            }
          }

          @page {
            size: ${paperSize} auto;
            margin: 0;
          }

          @media (max-width: 480px) {
            .vakra-print-preview-page {
              padding-left: 6px;
              padding-right: 6px;
            }

            .vakra-print-shell {
              max-width: calc(100vw - 12px);
            }

            .vakra-print-actions {
              padding: 8px;
            }

            .vakra-print-actions button {
              padding: 10px 12px;
              font-size: 13px;
            }

            .vakra-share-fab {
              right: 12px;
              bottom: 12px;
              width: 52px;
              height: 52px;
            }
          }

          @media print {
            html,
            body,
            #root {
              width: ${paperSize} !important;
              max-width: ${paperSize} !important;
              min-width: ${paperSize} !important;
              margin: 0 !important;
              padding: 0 !important;
              background: #ffffff !important;
              overflow: visible !important;
            }

            body * {
              visibility: hidden !important;
            }

            .vakra-print-shell,
            .vakra-print-shell * {
              visibility: visible !important;
            }

            .vakra-print-actions,
            .vakra-print-note,
            .vakra-share-fab,
            .screen-actions,
            .print-preview-actions,
            button.back-btn,
            button.print-btn {
              display: none !important;
              visibility: hidden !important;
            }

            .vakra-print-preview-page {
              min-height: auto !important;
              width: ${paperSize} !important;
              max-width: ${paperSize} !important;
              min-width: ${paperSize} !important;
              margin: 0 !important;
              padding: 0 !important;
              background: #ffffff !important;
              overflow: visible !important;
            }

            .vakra-print-shell {
              position: static !important;
              width: ${paperSize} !important;
              max-width: ${paperSize} !important;
              min-width: ${paperSize} !important;
              margin: 0 !important;
              padding: 0 !important;
              box-shadow: none !important;
              background: #ffffff !important;
              overflow: visible !important;
            }

            .vakra-print-content {
              width: 100% !important;
              max-width: 100% !important;
              min-width: 0 !important;
              margin: 0 !important;
              padding: 0 !important;
              background: #ffffff !important;
              overflow: visible !important;
            }

            .vakra-print-content .kot,
            .vakra-print-content .bill,
            .vakra-print-content .receipt,
            .vakra-print-content .kot-print,
            .vakra-print-content .bill-print,
            .vakra-print-content .thermal-bill,
            .vakra-print-content .print-page {
              width: 100% !important;
              max-width: 100% !important;
              min-width: 0 !important;
              margin: 0 !important;
              padding-left: 2mm !important;
              padding-right: 2mm !important;
              box-shadow: none !important;
              background: #ffffff !important;
              overflow: visible !important;
            }

            .vakra-print-content table {
              width: 100% !important;
              max-width: 100% !important;
              table-layout: fixed !important;
            }

            .vakra-print-content td,
            .vakra-print-content th {
              white-space: normal !important;
              overflow-wrap: anywhere !important;
              word-break: break-word !important;
            }
          }
        `}
      </style>

      <div className="vakra-print-actions">
        <button type="button" className="vakra-print-back" onClick={goBackToOrders}>
          Back to Orders
        </button>

        <button type="button" className="vakra-print-now" onClick={handlePrint}>
          Print Bill
        </button>
      </div>

      <div className="vakra-print-shell" ref={printShellRef}>
        <div
          className="vakra-print-content"
          dangerouslySetInnerHTML={{ __html: bodyHtml }}
        />
      </div>

      <button
        type="button"
        className="vakra-share-fab"
        onClick={handleShareBill}
        disabled={shareLoading}
        aria-label="Share bill"
        title="Share Bill"
      >
        {shareLoading ? (
          <span className="vakra-share-spinner" aria-hidden="true" />
        ) : (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7a3.12 3.12 0 0 0 0-1.39l7.05-4.11A3 3 0 1 0 15 5c0 .23.03.45.08.66L8.03 9.77a3 3 0 1 0 0 4.46l7.12 4.15c-.04.2-.07.41-.07.62a3 3 0 1 0 3-2.92Z" />
          </svg>
        )}
      </button>

      <p className="vakra-print-note">
        {printStarted
          ? 'Print complete ya cancel karne ke baad page automatic Orders Received par wapas aa jayega.'
          : `Is page par sirf bill/KOT print hoga. Paper size: ${paperSize}. Orders Queue UI print nahi hoga.`}
      </p>
    </div>
  );
};

export default PrintPreview;
